import React, { SyntheticEvent, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConfigurationFeatureFlag } from '@tink/config/src/tinkLinkConfig';
import { TinkLogo } from '@tink/core/src/components/atoms/TinkLogo/TinkLogo';
import { Provider, ProviderEntity } from '../../../../../models/provider';
import { ScopeItem } from '../../../../../lib-api/services/Oauth';
import { UnderlinedTextButton } from '../../../../atoms/Button/TextButton';
import { DocumentOverlayLink } from '../molecules/DocumentOverlayLink/DocumentOverlayLink';
import { getLanguageCode } from '../../../../../utilities/utils';
import { PageView } from '../../../../../models/analytics';
import { useUrlQuery } from '../../../../ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { AppConsentClientLegal } from '../../../../../connectivity/screens/consent/AppConsentClientLegal';
import { getTermsAndConditionsMarketUrl } from '../../../../../core/screens/Legal/TermsAndConditions/TermsAndConditionsMarket';
import { getPrivacyPolicyMarketUrl } from '../../../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicyMarket';
import { ScopesDetailOverlay } from './molecules/ScopesDetailOverlay/ScopesDetailOverlay';
import { ThirdPartyLabel } from './molecules/ThirdPartyLabel/ThirdPartyLabel';
import { messages } from './messages';

export type TinkLegalDisclaimerProps = {
  clientName: string;
  clientUrl: string;
  provider: ProviderEntity | undefined;
  scopesDescriptionsList: ScopeItem[];
  featureFlags: ConfigurationFeatureFlag[];
};

export const TinkLegalDisclaimer: React.FC<TinkLegalDisclaimerProps> = ({
  provider,
  clientName,
  featureFlags,
  clientUrl,
  scopesDescriptionsList,
}) => {
  const { formatMessage } = useIntl();
  const { locale, clientId, market } = useUrlQuery();
  const languageCode = getLanguageCode(locale);
  const [isScopesDetailOverlayOpen, setScopesOverlayOpen] = useState(false);
  const scopesButtonRef = useRef<HTMLAnchorElement>(null);

  const openScopesModal = (event: SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setScopesOverlayOpen(true);
  };

  const closeScopesModal = () => {
    setScopesOverlayOpen(false);

    if (scopesButtonRef.current) {
      scopesButtonRef.current.focus();
    }
  };

  const hideLogo = featureFlags.includes(ConfigurationFeatureFlag.TINK_LOGO_HIDDEN);
  const hideScopes = featureFlags.includes(ConfigurationFeatureFlag.CREDENTIAL_SCOPES_HIDDEN);
  const hideLegalText = featureFlags.includes(ConfigurationFeatureFlag.CREDENTIAL_TERMS_HIDDEN);
  const shouldShowAppSpecificLegalDocs = featureFlags.includes(
    ConfigurationFeatureFlag.SHOW_APP_SPECIFIC_LEGAL_DOCS
  );

  return (
    <div data-testid="legal-disclaimer-auth">
      {!hideLegalText && (
        <>
          {!hideLogo && (
            <div>
              <TinkLogo
                height="13px"
                width="28px"
                color="greyLight"
                tColor="greyLight"
                data-testid="tink-logo"
              />
            </div>
          )}
          {shouldShowAppSpecificLegalDocs && (
            <AppConsentClientLegal
              clientId={clientId}
              clientName={clientName}
              locale={locale}
              market={market}
            />
          )}
          {!shouldShowAppSpecificLegalDocs && (
            <FormattedMessage
              {...messages.termsAndConditionsMessage}
              values={{
                privacyPolicy: (
                  <DocumentOverlayLink
                    documentUrl={getPrivacyPolicyMarketUrl({ market, language: languageCode })}
                    pageView={PageView.PRIVACY_POLICY_SCREEN}
                    overlayAriaLabel={formatMessage(messages.privacyPolicy)}
                    testId="legal-privacy-policy-link"
                  >
                    {formatMessage(messages.privacyPolicy)}
                  </DocumentOverlayLink>
                ),
                termsAndConditions: (
                  <DocumentOverlayLink
                    documentUrl={getTermsAndConditionsMarketUrl({ market, language: languageCode })}
                    pageView={PageView.TERMS_AND_CONDITION_SCREEN}
                    overlayAriaLabel={formatMessage(messages.termsAndConditions)}
                    testId="legal-terms-and-conditions-link"
                  >
                    {formatMessage(messages.termsAndConditions)}
                  </DocumentOverlayLink>
                ),
              }}
            />
          )}
        </>
      )}{' '}
      {!hideScopes && (
        <>
          <ThirdPartyLabel
            clientName={clientName}
            clientUrl={clientUrl}
            isBusiness={provider && Provider.isBusiness(provider)}
            scopeModalTrigger={
              <UnderlinedTextButton
                onClick={openScopesModal}
                as="a"
                role="button"
                tabIndex={0}
                href=""
                ref={scopesButtonRef}
              >
                <FormattedMessage {...messages.readMore} />
              </UnderlinedTextButton>
            }
          />
          {isScopesDetailOverlayOpen && (
            <ScopesDetailOverlay close={closeScopesModal} scopes={scopesDescriptionsList} />
          )}
        </>
      )}
    </div>
  );
};
